import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";

// Archive reasons — controlled set matching backend _ARCHIVE_REASONS.
const ARCHIVE_REASONS = [
  { value: "not_client_related",          label: "Not client related" },
  { value: "incorrect_meeting_association", label: "Incorrect meeting association" },
  { value: "duplicate",                   label: "Duplicate" },
  { value: "test_internal_meeting",       label: "Test/internal meeting" },
  { value: "no_actionable_tax_service",   label: "No actionable tax service" },
  { value: "other",                       label: "Other (add note below)" },
];

function ArchiveDialog({ transcriptId, onConfirm, onCancel, busy }) {
  const [reason, setReason] = useState("not_client_related");
  const [note,   setNote]   = useState("");
  const noteRequired = reason === "other" && !note.trim();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="ca-archive-dialog">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-9 w-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
            <Icons.Archive className="h-4.5 w-4.5 text-amber-500" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900" data-testid="ca-archive-dialog-title">Archive this Estimate Review?</h2>
            <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
              This review will be removed from active Estimate Review queues and will no longer
              require staff attention. The underlying review and source evidence will be preserved
              for audit purposes.
            </p>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Reason (optional)</p>
          <div className="space-y-2">
            {ARCHIVE_REASONS.map((r) => (
              <label key={r.value} className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="radio" name="archive-reason" value={r.value}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                  className="accent-blue-600"
                  data-testid={`ca-archive-reason-${r.value}`}
                />
                <span className="text-sm text-slate-700">{r.label}</span>
              </label>
            ))}
          </div>
        </div>

        {reason === "other" && (
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Describe why this review is not relevant…"
            rows={2}
            data-testid="ca-archive-note"
            className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 mb-4 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
        )}

        <div className="flex flex-wrap gap-2 justify-end">
          <button
            onClick={onCancel}
            data-testid="ca-archive-cancel"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 px-4 py-2 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
          >
            Cancel
          </button>
          {transcriptId && (
            <button
              onClick={() => onConfirm(reason, note, true)}
              disabled={noteRequired || busy}
              data-testid="ca-archive-with-meeting-btn"
              className="text-sm font-medium text-amber-700 hover:text-white hover:bg-amber-600 border border-amber-300 hover:border-amber-600 px-3 py-2 rounded-lg transition-colors disabled:opacity-40"
            >
              Archive Review + Mark Meeting Not Client Related
            </button>
          )}
          <button
            onClick={() => onConfirm(reason, note, false)}
            disabled={noteRequired || busy}
            data-testid="ca-archive-confirm-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 px-4 py-2 rounded-lg shadow-sm transition-colors disabled:opacity-40"
          >
            {busy ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.Archive className="h-3.5 w-3.5" />}
            Archive Review
          </button>
        </div>
      </div>
    </div>
  );
}

// Small circular progress ring (SVG) — presentation only.
function Ring({ value, size = 40, stroke = 4, color = "#059669", track = "#e2e8f0", children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, value)) / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: "stroke-dashoffset .5s ease" }} />
      </svg>
      {children && <span className="absolute inset-0 flex items-center justify-center">{children}</span>}
    </div>
  );
}

function IntelCard({ icon: Icon, tint, label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3.5 flex items-start gap-3" data-testid={testid}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${tint}`}><Icon className="h-4.5 w-4.5" /></div>
      <div className="min-w-0">
        <div className="text-[11px] font-medium text-slate-500 truncate">{label}</div>
        <div className="text-xl font-bold text-slate-900 leading-tight">{value}</div>
        {sub && <div className="text-[11px] text-slate-400 truncate">{sub}</div>}
      </div>
    </div>
  );
}

export function ReviewOverview({ data, rec, counts, confidence, complexity, clientName, onOpenClient, onContinue, detail, analysisId, transcriptId, isArchived, onArchive, archiveBusy }) {
  const navigate = useNavigate();
  const [showIntel, setShowIntel] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);
  const { reviewedCount, reviewableCount, riskCount, missingCount, pendingCount, otherCount = 0, irsDetected } = counts;
  const pct = reviewableCount ? Math.round((reviewedCount / reviewableCount) * 100) : 0;
  const prepared = data.created_at ? new Date(data.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—";

  const research = data.research_package || {};
  const ctx = data.case_context_package || {};
  const authorities = (research.authorities || []).length;
  const conversations = ctx.metrics?.records_retrieved ?? (ctx.relevant_historical_facts || []).length;
  const documents = data.documents_reviewed ?? (ctx.supporting_sources || []).length;

  // Consolidated "Key Items to Review" — one source of truth, driven by real counts.
  const keyItems = [
    irsDetected && { icon: Icons.ShieldAlert, tint: "text-rose-500", label: "IRS representation may be needed", count: 1 },
    { icon: Icons.UserCog, tint: "text-blue-500", label: "Client record updates pending", count: pendingCount },
    { icon: Icons.FileWarning, tint: "text-amber-500", label: "Missing documents", count: missingCount },
    { icon: Icons.AlertTriangle, tint: "text-amber-500", label: "Risk items to review", count: riskCount },
    otherCount > 0 && { icon: Icons.Info, tint: "text-slate-400", label: "Other items for your consideration", count: otherCount },
  ].filter(Boolean).filter((i) => i.count > 0);

  const RecIcon = Icons[rec.recIcon] || Icons.AlertTriangle;
  const toneMap = {
    "Reject & Re-run Analysis": { text: "text-rose-600", bg: "bg-rose-50" },
    "Review Before Approving": { text: "text-amber-600", bg: "bg-amber-50" },
    "Approve After Minor Edits": { text: "text-blue-600", bg: "bg-blue-50" },
    Approve: { text: "text-emerald-600", bg: "bg-emerald-50" },
  };
  const tone = toneMap[rec.recommendation] || { text: "text-amber-600", bg: "bg-amber-50" };

  return (
    <>
    <div className="space-y-5 mb-6">
      {/* ---------- Header ---------- */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="ca-title">Estimate Review</h1>
          <p className="text-sm text-slate-400 mt-0.5">Prepared by Allison</p>
          <div className="flex items-center gap-6 mt-3 text-sm flex-wrap">
            <span className="text-slate-500">Client: <button onClick={onOpenClient} className="font-semibold text-blue-600 hover:underline" data-testid="ca-client-link">{clientName}</button></span>
            <span className="text-slate-500">Prepared: <span className="text-slate-700 font-medium">{prepared}</span></span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500" data-testid="ca-complexity">
            <Icons.Users className="h-4 w-4 text-slate-400" />{complexity} Matter
          </span>
          <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-500" data-testid="ca-confidence-header">
            Confidence
            <Ring value={confidence} size={38} stroke={4} color={confidence < 85 ? "#f59e0b" : "#059669"}>
              <span className="text-[10px] font-bold text-slate-700">{confidence}%</span>
            </Ring>
          </span>
          {/* View Source Meeting — shown when analysis has a linked transcript */}
          {data.transcript_id && (
            <button
              onClick={() => navigate(`/meeting-review/${data.transcript_id}`)}
              data-testid="ca-view-source-meeting-btn"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-[#2F80ED] border border-slate-200 hover:border-[#2F80ED]/40 hover:bg-blue-50 rounded-lg px-2.5 py-1.5 transition-colors"
              title="Open the source Zoom meeting in Meeting Review"
            >
              <Icons.Video className="h-3.5 w-3.5" />
              Source Meeting
            </button>
          )}
          {/* ⋯ Archive menu — only shown when review is not yet archived */}
          {!isArchived && analysisId && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                data-testid="ca-archive-menu-btn"
                className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors text-slate-500 hover:text-slate-700"
                title="More actions"
              >
                <Icons.MoreHorizontal className="h-4 w-4" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl border border-slate-200 shadow-lg z-20 py-1">
                  <button
                    onClick={() => { setMenuOpen(false); setArchiveDialogOpen(true); }}
                    data-testid="ca-archive-menu-item"
                    className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <Icons.Archive className="h-4 w-4 text-slate-400 shrink-0" />
                    Archive / Not Relevant
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ---------- Review Summary + Progress ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="ca-review-summary">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-4">Review Summary</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className={`h-12 w-12 rounded-full flex items-center justify-center mb-3 ${tone.bg}`}>
                <RecIcon className={`h-6 w-6 ${tone.text}`} />
              </div>
              <h2 className={`text-xl font-bold ${tone.text}`} data-testid="ca-summary-status">{rec.recommendation}</h2>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{rec.explanation || rec.nextAction}</p>
              <button onClick={onContinue} data-testid="ca-continue-review"
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors">
                Continue Review <Icons.ChevronRight className="h-4 w-4" />
              </button>
            </div>
            {keyItems.length > 0 && (
              <div className="md:border-l md:border-slate-100 md:pl-6">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">Key Items to Review</div>
                <ul className="space-y-2.5" data-testid="ca-key-items">
                  {keyItems.map((it, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm">
                      <it.icon className={`h-4 w-4 shrink-0 ${it.tint}`} />
                      <span className="text-slate-600 flex-1">{it.label}</span>
                      <span className="text-sm font-semibold text-slate-700 tabular-nums">{it.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="ca-progress">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-4">Review Progress</div>
          <div className="flex items-center gap-4">
            <Ring value={pct} size={72} stroke={6}>
              <span className="text-center leading-none"><span className="block text-xl font-bold text-slate-900">{reviewedCount}</span><span className="block text-[10px] text-slate-400">of {reviewableCount}</span></span>
            </Ring>
            <div>
              <div className="text-sm font-semibold text-slate-800">Sections Completed</div>
              <div className="text-xs text-slate-400 mt-0.5">{pct >= 100 ? "All sections reviewed." : "Keep going — you're making progress."}</div>
            </div>
          </div>
          <div className="mt-4 flex gap-1" aria-hidden>
            {Array.from({ length: reviewableCount }).map((_, i) => (
              <span key={i} className={`h-1.5 flex-1 rounded-full ${i < reviewedCount ? "bg-emerald-500" : "bg-slate-200"}`} />
            ))}
          </div>
        </div>
      </div>

      {/* ---------- Case Intelligence ---------- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ca-case-intelligence">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Case Intelligence</div>
          {detail && (
            <button onClick={() => setShowIntel((s) => !s)} data-testid="ca-view-all-intel" className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1">
              View all case intelligence <Icons.ChevronDown className={`h-3.5 w-3.5 transition-transform ${showIntel ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <IntelCard testid="ca-intel-authorities" icon={Icons.Landmark} tint="bg-slate-100 text-slate-600" label="Tax Authorities" value={authorities} sub="Sources reviewed" />
          <IntelCard testid="ca-intel-conversations" icon={Icons.MessagesSquare} tint="bg-violet-100 text-violet-600" label="Historical Conversations" value={conversations} sub="Communications analyzed" />
          <IntelCard testid="ca-intel-findings" icon={Icons.Sparkles} tint="bg-emerald-100 text-emerald-600" label="AI Findings" value={riskCount} sub="Potential issues identified" />
          <IntelCard testid="ca-intel-documents" icon={Icons.FileText} tint="bg-blue-100 text-blue-600" label="Documents Reviewed" value={documents} sub="Total documents" />
        </div>
        {showIntel && detail && <div className="mt-4 pt-4 border-t border-slate-100">{detail}</div>}
      </div>
    </div>
    {archiveDialogOpen && (
      <ArchiveDialog
        transcriptId={transcriptId}
        onConfirm={(reason, note, alsoMeeting) => { setArchiveDialogOpen(false); onArchive?.(reason, note, alsoMeeting); }}
        onCancel={() => setArchiveDialogOpen(false)}
        busy={archiveBusy}
      />
    )}
  </>
  );
}
